import { useState } from 'react';
import type { LLMTextProvider, LLMEmbedProvider } from '@shared/types';
import { useProfileSettings } from './ProfileSettingsContext';
import { batchProcessResumes } from '../../services/resumeMatchingFunctions';

export default function GeneralSettingsTab() {
  const {
    settings, setSettings, saveSettings, saving,
    llmTextProvider, setLlmTextProvider,
    llmTextApiKey, setLlmTextApiKey,
    llmEmbedProvider, setLlmEmbedProvider,
    llmEmbedApiKey, setLlmEmbedApiKey,
    llmHasTextKey, llmHasEmbedKey,
    pineconeApiKey, setPineconeApiKey,
    pineconeIndex, setPineconeIndex,
    llmHasPineconeKey,
    saveLLMConfig,
    setMessage
  } = useProfileSettings();

  const [showEmbedSection, setShowEmbedSection] = useState(!!llmEmbedProvider);
  const [reprocessing, setReprocessing] = useState(false);

  const handleReprocessResumes = async () => {
    if (!window.confirm('This will re-calculate embeddings for all your resumes. This may take a while and consume API tokens. Continue?')) return;
    setReprocessing(true);
    try {
      // Need a way to get userId here, or maybe context should provide it
      // For now, assume context might need UID or it's handled in the service
      // We'll use a hack or update context later if needed
      await batchProcessResumes();
      setMessage({ type: 'success', text: 'All resumes have been re-indexed with the new embedding provider.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: `Reprocessing failed: ${err.message}` });
    } finally {
      setReprocessing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Account Settings */}
      <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Account Preferences</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Theme</label>
              <p className="text-xs text-gray-500">Choose your preferred appearance</p>
            </div>
            <select
              value={settings.theme}
              onChange={(e) => setSettings({ ...settings, theme: e.target.value as any })}
              className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-sm rounded-lg p-2"
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
              <option value="system">System</option>
            </select>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Daily Goal</label>
              <p className="text-xs text-gray-500">Target job applications per day</p>
            </div>
            <input
              type="number"
              value={settings.dailyApplicationGoal}
              onChange={(e) => setSettings({ ...settings, dailyApplicationGoal: parseInt(e.target.value) })}
              className="w-20 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-sm rounded-lg p-2"
            />
          </div>
        </div>
        
        <div className="mt-6 flex justify-end">
          <button
            onClick={saveSettings}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          >
            {saving ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
      </section>

      {/* LLM Configuration */}
      <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-l-4 border-blue-500">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"></path></svg>
            AI Engine (BYOK)
          </h3>
          <span className="text-[10px] bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded-full font-bold uppercase">Bring Your Own Key</span>
        </div>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Text Generation Provider</label>
              <select
                value={llmTextProvider}
                onChange={e => setLlmTextProvider(e.target.value as LLMTextProvider)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-lg text-sm"
              >
                <option value="gemini">Google Gemini (Flash/Pro)</option>
                <option value="openai">OpenAI (GPT-4o/GPT-3.5)</option>
                <option value="anthropic">Anthropic (Claude 3)</option>
                <option value="custom">Custom (Ollama/Local)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">
                API Key {llmHasTextKey && <span className="text-green-600 dark:text-green-400 font-bold ml-1">✓</span>}
              </label>
              <input
                type="password"
                value={llmTextApiKey}
                onChange={e => setLlmTextApiKey(e.target.value)}
                placeholder={llmHasTextKey ? '••••••••••••••••' : 'Enter API key'}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-lg text-sm"
              />
            </div>
          </div>

          <div className="pt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showEmbedSection}
                onChange={e => setShowEmbedSection(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Enable advanced semantic matching (Resume Architect)</span>
            </label>
          </div>

          {showEmbedSection && (
            <div className="mt-3 space-y-4 p-4 bg-gray-50 dark:bg-gray-900/40 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Embedding Provider</label>
                  <select
                    value={llmEmbedProvider}
                    onChange={e => setLlmEmbedProvider(e.target.value as LLMEmbedProvider | '')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-lg text-sm"
                  >
                    <option value="">— select —</option>
                    <option value="gemini">Google Gemini</option>
                    <option value="openai">OpenAI</option>
                    <option value="voyage">Voyage AI</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">
                    Embedding Key {llmHasEmbedKey && <span className="text-green-600 dark:text-green-400 font-bold ml-1">✓</span>}
                  </label>
                  <input
                    type="password"
                    value={llmEmbedApiKey}
                    onChange={e => setLlmEmbedApiKey(e.target.value)}
                    placeholder={llmHasEmbedKey ? '••••••••••••••••' : 'Enter API key'}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Pinecone Index</label>
                  <input
                    type="text"
                    value={pineconeIndex}
                    onChange={e => setPineconeIndex(e.target.value)}
                    placeholder="e.g. resume-index"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">
                    Pinecone API Key {llmHasPineconeKey && <span className="text-green-600 dark:text-green-400 font-bold ml-1">✓</span>}
                  </label>
                  <input
                    type="password"
                    value={pineconeApiKey}
                    onChange={e => setPineconeApiKey(e.target.value)}
                    placeholder={llmHasPineconeKey ? '••••••••••••••••' : 'Enter API key'}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-lg text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              onClick={saveLLMConfig}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              Save AI Configuration
            </button>
            {llmHasTextKey && (
              <button
                onClick={handleReprocessResumes}
                disabled={reprocessing}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium"
              >
                {reprocessing ? 'Processing...' : 'Re-index library'}
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
